import { UpdateProductButton } from '@commerce-cart-webcomponents/ui-composite';
import {
  ProjectionType,
  logObject,
  productApi,
  productDataTransformer,
  type OptionMap,
  type VariantGroup,
  type DerivedAvailabilityStatusInternal,
  type ProductData,
} from '@commerce-product-webcomponents/sdk';
import {
  BasicDetailsComponent,
  DerivedAvailabilityStatusComponentGroupKey,
  DetailsType,
  ImageComponent,
  PriceComponent,
  PriceType,
  Variant,
  VariantStyleType,
  checkIfProductIsNya,
  includesDerivedAvailabilityStockStatus,
  getFulfillmentType,
} from '@commerce-product-webcomponents/ui';
import { Drawer, useNextgenRouter, useSession, useTranslation } from '@nextgen-web-framework/all';
import { Logger } from '@nextgen-web-framework/core';
import { FC, useEffect, useState } from 'react';
import styles from './CartDrawer.module.scss';
import type { CartDrawerPropsTypes } from './types/propsTypes';

const CartDrawer: FC<CartDrawerPropsTypes> = (props: CartDrawerPropsTypes): JSX.Element => {
  const {
    link,
    alt,
    brandName,
    productName,
    productPrice,
    currency,
    productId,
    onClose,
    strikethroughPrice,
    entryId,
    derivedAvailabilityStockStatus,
    variantGroup,
    subscriptionVariantActions,
    showPriceLabel = true,
    showZeroAsFree = true,
  } = props;
  const isVariantGroupAvailable = !!variantGroup;
  const [closeDrawer, setCloseDrawer] = useState<boolean>(false);
  const [productState, setProductState] = useState({
    brandName,
    productName,
    link,
    productPrice,
    currency,
    alt,
    productId,
    variantName: '',
    variantGroup,
    derivedAvailabilityStatus: false,
    derivedAvailabilityStockStatus,
    restrictFromAddToCartFlag: false,
  });
  const loggerInstance = Logger.getInstance(logObject('CartDrawer'));

  const router = useNextgenRouter();
  const selectedLanguage = router.params?.locale;

  const {
    session: { warehouseId = '' },
  } = useSession();

  useEffect(() => {
    if (productId && !isVariantGroupAvailable) {
      getProductById(productId);
    }
  }, [productId, selectedLanguage, warehouseId]);

  const onHandleClick = (e: React.KeyboardEvent | React.MouseEvent | React.TouchEvent) => {
    if (onClose && typeof onClose === 'function') {
      onClose(e);
    }
    setCloseDrawer(true);
  };

  const transformString = (str: string) => str[0].toUpperCase() + str.substring(1, str.length);

  const getVariantName = (itemNumber: string, variantGroup?: VariantGroup) => {
    const variant = variantGroup?.optionMap?.filter((om) => om?.product?.items?.[0].itemNumber === itemNumber);
    const vg = variant?.[0]?.optionChoice?.split('_');
    return (vg?.length || 0) === 1 ? transformString(vg?.[0] ?? '') : `${transformString(vg?.[0] ?? '')}`;
  };

  const getProductById = (_productId: string) => {
    productApi
      .getProductByIdOrError({
        productId: _productId,
        projections: ProjectionType.Detailed,
        warehouseId,
        fulfillmentType: getFulfillmentType(),
      })
      .then((productData: ProductData | Response) => {
        if (!(productData instanceof Response)) {
          const pd = productDataTransformer(productData);
          setProductState({
            brandName: pd.brandName,
            productName: pd.productName,
            link: pd.mainLink,
            productPrice: pd.productPrice,
            currency: pd.currency,
            alt: pd.alt,
            productId: pd.productId,
            variantName: getVariantName(pd.productId, pd.variantData),
            variantGroup: isVariantGroupAvailable ? variantGroup : pd.variantData,
            derivedAvailabilityStatus: includesDerivedAvailabilityStockStatus(
              pd.derivedAvailabilityStatus,
              DerivedAvailabilityStatusComponentGroupKey.CTA_ENABLED_STATUSES,
            ),
            derivedAvailabilityStockStatus: pd.derivedAvailabilityStatus,
            restrictFromAddToCartFlag: pd.productFlags?.restrictFromAddToCart || false,
          });
        }
      })
      .catch((err: Error) => {
        loggerInstance.error(err);
      });
  };
  const t = useTranslation();

  const handleDataRefresh = (optionMapSelected: OptionMap | undefined) => {
    getProductById(optionMapSelected?.product?.items?.[0]?.itemNumber ?? '');
  };

  return (
    <div className={styles['cart-drawer-container']}>
      {!closeDrawer && (
        <Drawer
          header={<h5 className={styles['cart-drawer-heading']}>{t('product.VariantDrawer.heading')}</h5>}
          closeOnBackdropClick={true}
          closeIcon={true}
          onClose={onHandleClick}
          bottomGap={0}
          footer={
            <div className={styles['cart-drawer-update-btn']}>
              {subscriptionVariantActions ? (
                subscriptionVariantActions(productState.productId, onHandleClick)
              ) : (
                <UpdateProductButton
                  disabled={
                    productId === productState.productId ||
                    !productState.derivedAvailabilityStatus ||
                    checkIfProductIsNya(
                      productState.derivedAvailabilityStockStatus as DerivedAvailabilityStatusInternal,
                    ) ||
                    productState.restrictFromAddToCartFlag
                  }
                  data={{
                    productId: productId,
                    updatedProductId: productState.productId,
                    entryId: entryId,
                  }}
                  onProductUpdated={() => onHandleClick(new Event('keyboard') as unknown as React.KeyboardEvent)}
                />
              )}
            </div>
          }
        >
          <div className={styles['cpw-cart-card-content']} data-testid="cart-drawer-data-testid">
            <div className={styles['product-container']}>
              <div className={styles['product-image']}>
                <ImageComponent link={productState.link} alt={productState.alt} />
              </div>
              <div>
                <BasicDetailsComponent
                  brandName={productState.brandName}
                  productName={productState.productName}
                  detailsType={DetailsType.VARIANT_DRAWER_CARD}
                />
                {showPriceLabel && (
                  <PriceComponent
                    productPrice={productState.productPrice}
                    currency={productState.currency}
                    strikethroughPrice={strikethroughPrice}
                    priceType={PriceType.PRICE_VARIANT_CARD}
                    showZeroAsFree={showZeroAsFree}
                  />
                )}
              </div>
            </div>
            <div className={styles['product-container-border']}></div>
            <div className={styles['variant-selection']}>
              <Variant
                variantData={productState.variantGroup}
                handleDataRefresh={handleDataRefresh}
                defaultVariantItemId={productState.productId}
                variantStyleType={VariantStyleType.VARIANT_DRAWER}
              />
            </div>
          </div>
        </Drawer>
      )}
    </div>
  );
};

export default CartDrawer;
export type { CartDrawerPropsTypes };
